import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  throwError,
  from,
  of,
} from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ToastrService } from 'ngx-toastr';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { Router } from '@angular/router';
import { PermissionService } from 'src/app/core/services/permission.service';
import { MonitoringLogService } from 'src/app/core/services/monitoring-log.service';
import { isConfirmRequiredDuplicateError } from 'src/app/core/utils/contact-api-error.util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private localization: LocalizationService,
    private toastr: ToastrService,
    private router: Router,
    private permissionService: PermissionService,
    private monitoringLog: MonitoringLogService
  ) { }


  getCurrentLanguage() {
    return this.localization.getCachedLanguage() ?? 'en';
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const accessToken = this.authService.getAccessToken();
    const refreshToken = this.authService.getRefreshToken();

    const excludedEndpoints = [
      '/api/v1/Authentication/SignIn',
      '/api/v1/Authentication/Refresh-Token',
      '/api/v1/Authentication/ForgetPassword',
      'api/v1/Authentication/confirm-code',
      'api/v1/Authentication/ResetPassword',
      'api/v1/User/SelfRegistr',
      '/api/v1/Authentication/forgot-password',
    ];

    const isExcluded = excludedEndpoints.some((endpoint) =>
      request.url.includes(endpoint)
    );

    if (!isExcluded && accessToken) {
      const clonedRequest = this.addToken(request, accessToken);
      if (this.authService.isTokenExpired()) {
        if (!refreshToken) {
          return next.handle(clonedRequest).pipe(
            catchError((error: HttpErrorResponse) => {
              this.handleErrors(error, request);
              return throwError(() => error);
            })
          );
        }

        if (!this.isRefreshing) {
          this.isRefreshing = true;
          this.refreshTokenSubject.next(null);

          return from(this.authService.refreshToken()).pipe(
            switchMap((refreshSuccess) => {
              if (!refreshSuccess || refreshSuccess.data === null || !refreshSuccess.data.accessToken) {
                this.authService.logout();
                return throwError(() => new Error('Refresh token failed'));
              }

              const newToken = refreshSuccess.data.accessToken;
              this.authService.setToken(newToken);
              this.authService.setRefreshToken(refreshSuccess.data.refreshToken);
              this.authService.setTokenExpiration(refreshSuccess.data.expireAt);
              if (refreshSuccess.data.validUpto) this.authService.setValidUpto(refreshSuccess.data.validUpto);

              this.refreshTokenSubject.next(newToken);
              this.isRefreshing = false;

              const refreshedRequest = this.addToken(request, newToken);
              return next.handle(refreshedRequest);
            }),
            catchError((error: HttpErrorResponse) => {
              this.isRefreshing = false;
              this.authService.logout();
              return throwError(() => error);
            })
          );
        }

        return this.refreshTokenSubject.pipe(
          filter((token) => token !== null),
          take(1),
          switchMap((newToken) => {
            const refreshedRequest = this.addToken(request, newToken as string);
            return next.handle(refreshedRequest);
          }),
          catchError((error: HttpErrorResponse) => {
            this.handleErrors(error, request);
            return throwError(() => error);
          })
        );
      }

      return next.handle(clonedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleErrors(error, request);
          return throwError(() => error);
        })
      );
    } else {
      const clonedRequest = request;
      return next.handle(clonedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleErrors(error, request);
          return throwError(() => error);
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      headers: request.headers
        .set('Authorization', 'Bearer ' + token)
        .set('Cache-Control', 'public, max-age=31536000'),
    });
  }

  private handleErrors(error: HttpErrorResponse | any, request?: HttpRequest<any>): void {
    console.log(error, " errors");

    const errorCode = error?.error?.StatusCode || error?.status;
    const suppressConfirmRequiredToast = isConfirmRequiredDuplicateError(error);
    // Check if error messages should be suppressed
    const suppressErrorMessage =
      request?.headers.get('X-Suppress-Error-Message') === 'true' ||
      suppressConfirmRequiredToast;
    
    if (!suppressErrorMessage) {
      const errorsArray = error?.error?.Errors ?? [];
      if (errorsArray.length === 0) {
        errorsArray.push(error?.error?.message ?? error?.error?.Message ?? error?.message ?? error?.Message);
      }
      errorsArray.forEach((err: string) => {
        if (!!err)
          this.toastr.error(
            err,
            this.getCurrentLanguage() === 'ar' ? 'خطأ' : 'Error'
          );
      });
    }

    if (errorCode === 409) {
      this.handle409Error();
      return;
    }

    if (request && error instanceof HttpErrorResponse) {
      const st = error.status;
      if (st === 0 || st >= 500) {
        this.monitoringLog.logHttpTransportOrServerFailure(st, request.url);
      }
    }
  }

  private handle409Error(): void {

    // Step 1: Refresh token
    this.authService.refreshToken().pipe(
      switchMap((refreshSuccess) => {
        if (!refreshSuccess || refreshSuccess.data === null || !refreshSuccess.data.accessToken) {
          console.error('[AuthInterceptor] Refresh token failed on 409 - logging out');
          this.authService.logout();
          return of(null);
        }

        // Step 2: Update tokens
        const newToken = refreshSuccess.data.accessToken;
        this.authService.setToken(newToken);
        this.authService.setRefreshToken(refreshSuccess.data.refreshToken);
        this.authService.setTokenExpiration(refreshSuccess.data.expireAt);
        if (refreshSuccess.data.validUpto) this.authService.setValidUpto(refreshSuccess.data.validUpto);

        console.log('[AuthInterceptor] Token refreshed successfully on 409');

        // Step 3: Reload permissions after token refresh
        this.authService.invalidateUserPremissions();

        location.reload();

        return of(null);
      }),
      catchError((refreshError: HttpErrorResponse) => {
        console.error('[AuthInterceptor] Error refreshing token on 409:', refreshError);
        // If refresh fails, logout user
        this.authService.logout();
        return of(null);
      })
    ).subscribe({
      next: () => {
        console.log('[AuthInterceptor] 409 handled successfully - token refreshed and permissions reloaded');
      },
      error: (err) => {
        console.error('[AuthInterceptor] Failed to handle 409:', err);
      }
    });
  }
}
